import addColumnAfterIconContent from '../../../assets/icons20/addColumnAfter.svg';
import addColumnBeforeIconContent from '../../../assets/icons20/addColumnBefore.svg';
import addRowAfterIconContent from '../../../assets/icons20/addRowAfter.svg';
import addRowBeforeIconContent from '../../../assets/icons20/addRowBefore.svg';
import trashIconContent from '../../../assets/icons20/trash.svg';
import { EDITOR_SELECTED_ELEMENT } from '../../../helpers/dom/content';
import { getElementContents } from '../../../helpers/dom/element';
import { findAncestor, isElement } from '../../../helpers/dom/node';
import {
  getFirstColumnCells,
  getFirstNonEmptyRow,
  getMaxBorderLeftWidth,
  getMaxBorderTopWidth,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  TablePlan,
} from '../../../helpers/dom/table';
import { getEmotion } from '../../../helpers/emotion';
import { indexOf } from '../../../helpers/iterable';
import {
  createPointsWithSelectionInElement,
  updateSelectionInElementWithPoints,
} from '../../../helpers/selection';
import { getBorderLeftWidthForElement, getBorderTopWidthForElement } from '../../../helpers/style/border';

const TABLE_WRAPPER_CLASSNAME = 'editor-table-wrapper';
export const TABLE_CORNER_BUTTON = 'editor-table-corner-button-decorator';

export const headerClassnames = {
  HEADER: 'editor-header-decorator',
  COLUMN_DISPLAY: 'editor-column-header-decorator',
  ROW_DISPLAY: 'editor-row-header-decorator',
  HEADER_WRAPPER: 'editor-header-decorator-wrapper',
  WITH_HEADER: 'editor-decorated-cell-header',
};

const WITH_SELECTED_CELLS = 'editor-table-with-selected-cells';
export const TABLE_FULLY_SELECTED = 'editor-table-full-selection';

export const COLUMN_RESIZE_HANDLE = 'editor-table-column-resize-handle';

export const decoratedCellsClassnames = {
  BORDERED: 'editor-decorated-cell-bordered',
  BORDERED_ROW: 'editor-decorated-cell-bordered-row',
  BORDERED_COLUMN: 'editor-decorated-cell-bordered-column',
  BORDERED_TOP: 'editor-decorated-cell-border-top',
  BORDERED_RIGHT: 'editor-decorated-cell-border-right',
  BORDERED_BOTTOM: 'editor-decorated-cell-border-bottom',
  BORDERED_LEFT: 'editor-decorated-cell-border-left',
  BORDERED_ROUNDED: 'editor-decorated-cell-bordered-rounded',

  WITH_BUTTON_PANEL: 'editor-cell-with-button-panel',
};

export const buttonPanelClassnames = {
  PANEL: 'editor-button-panel',
  BUTTON: 'editor-button',
  COLUMN_DISPLAY: 'editor-column-button-panel',
  ROW_DISPLAY: 'editor-row-button-panel',
  BEFORE_DISPLAY: 'editor-button-panel-before',
  CORNER_BUTTON: 'editor-corner-button',
};

const HEADER_DECORATOR_SIZE = 12;
const BUTTON_HEIGHT = 26;
const BUTTON_MARGIN = 2;
const BUTTON_PANEL_PADDING = 3;

export const DEFAULT_BORDER_COLOR = '#c3c4c6';

export function isTableWrapper(node: Node): node is Element {
  return isElement(node) && node.tagName === 'SPAN' && node.classList.contains(TABLE_WRAPPER_CLASSNAME);
}

export function isDecoratedTable(node: Node): node is Element {
  return isTableElement(node) && node.parentElement !== null && isTableWrapper(node.parentElement);
}

export function isTableCornerButtonDecorator(node: Node): node is Element {
  return isElement(node) && node.tagName === 'SPAN' && node.classList.contains(TABLE_CORNER_BUTTON);
}

export function isHeaderDecorator(node: Node): node is Element {
  return isElement(node) && node.tagName === 'SPAN' && node.classList.contains(headerClassnames.HEADER);
}

export function isHeaderDecoratorDecorated(node: Node): node is Element {
  return (
    isHeaderDecorator(node) &&
    (node.classList.contains(decoratedCellsClassnames.BORDERED_ROW) ||
      node.classList.contains(decoratedCellsClassnames.BORDERED_COLUMN))
  );
}

export function isColumnHeaderDecorator(node: Node): node is Element {
  return isHeaderDecorator(node) && node.classList.contains(headerClassnames.COLUMN_DISPLAY);
}

export function isRowHeaderDecorator(node: Node): node is Element {
  return isHeaderDecorator(node) && node.classList.contains(headerClassnames.ROW_DISPLAY);
}

export function isButtonPanel(node: Node): node is Element {
  return isElement(node) && node.classList.contains(buttonPanelClassnames.PANEL);
}

export function isTableFullySelected(node: Node): node is Element {
  return isTableElement(node) && node.classList.contains(TABLE_FULLY_SELECTED);
}

export function isColumnResizeHandle(node: Node | null) {
  return isElement(node) && node.classList.contains(COLUMN_RESIZE_HANDLE);
}

/**
 * Add headers to each column/row.
 */
export function decorateTableForEdition(table: HTMLTableElement): void {
  const { ownerDocument: document, parentNode } = table;
  if (!document || !parentNode || isTableWrapper(parentNode)) {
    return;
  }

  const savedSelection = createPointsWithSelectionInElement(table);

  // Decorate table.
  const wrapper = document.createElement('span');
  wrapper.classList.add(TABLE_WRAPPER_CLASSNAME);
  // Not ideal since some new style will be appended to document's head each time this is called.
  // It'd be better to append it to `wrapper`. But currently Front is removing it when doing so.
  // TODO: append emotion to wrapper.
  const { css } = getEmotion(document);
  const maxBorderTopWidth = getMaxBorderTopWidth(table) || 0;
  const maxBorderLeftWidth = getMaxBorderLeftWidth(table) || 0;
  const style = getTableWrapperStyle(maxBorderLeftWidth, maxBorderTopWidth);
  wrapper.classList.add(
    css`
      ${style}
    `,
  );

  // Decorate first row / column.
  decorateColumnsHeaders(document, table);
  decorateRowHeaders(document, table);
  decorateTableCornerButton(document, table);

  parentNode.insertBefore(wrapper, table);
  wrapper.appendChild(table);
  updateSelectionInElementWithPoints(table, savedSelection);
}

/**
 * Add header to each column of table.
 */
function decorateColumnsHeaders(document: Document, table: HTMLTableElement) {
  const firstNonEmptyRow = getFirstNonEmptyRow(table);
  if (!document || !firstNonEmptyRow) {
    return;
  }
  Array.from(firstNonEmptyRow.children).forEach((cell, index, arr) => {
    if (!isTableCellElement(cell)) {
      return;
    }
    decorateTableHeader(document, table, cell, 'column', index === 0, index === arr.length - 1);
  });
}

/**
 * Add header to each row.
 */
function decorateRowHeaders(document: Document, table: HTMLTableElement) {
  getFirstColumnCells(table).forEach((cell, index, arr) => {
    decorateTableHeader(document, table, cell, 'row', index === 0, index === arr.length - 1);
  });
}

/**
 * Add a rounded header to the table (top left corner).
 */
function decorateTableCornerButton(document: Document, table: HTMLTableElement) {
  const [firstRow] = Array.from(table.rows);
  const [firstCell] = Array.from(firstRow.cells);

  // Create handle wrapper so that cursor cannot reach handle level.
  const handleWrapper = firstCell.querySelector(`.${headerClassnames.HEADER_WRAPPER}`);
  if (!handleWrapper) {
    return;
  }
  // Create button
  const handle = document.createElement('span');
  handle.classList.add(TABLE_CORNER_BUTTON);
  handleWrapper.appendChild(handle);
  firstCell.appendChild(handleWrapper);
}

export function updateTableHeaders(table: HTMLTableElement) {
  const document = table.ownerDocument;
  if (document) {
    undecorateTableHeaders(table);
    decorateColumnsHeaders(document, table);
    decorateRowHeaders(document, table);
  }
}

export function decorateTableHeader(
  document: Document,
  table: HTMLTableElement,
  cell: HTMLTableCellElement,
  mode: 'row' | 'column',
  isFirstCell: boolean,
  isLastCell: boolean,
) {
  cell.classList.add(headerClassnames.WITH_HEADER);

  // Create handle wrapper so that cursor cannot reach handle level.
  const maybeExistingHandleWrapper = cell.querySelector(`.${headerClassnames.HEADER_WRAPPER}`);
  const handleWrapper = maybeExistingHandleWrapper || document.createElement('span');
  if (!maybeExistingHandleWrapper) {
    handleWrapper.setAttribute('contenteditable', 'false');
    handleWrapper.classList.add(headerClassnames.HEADER_WRAPPER);
  }
  // Create handle
  const handle = document.createElement('span');
  handle.classList.add(headerClassnames.HEADER);
  handle.classList.add(mode === 'row' ? headerClassnames.ROW_DISPLAY : headerClassnames.COLUMN_DISPLAY);
  // Create handle's style.
  const { css } = getEmotion(document);
  const customStyle = getHeaderDecoratorStyle(table, cell, isFirstCell, isLastCell, mode);
  handle.classList.add(
    css`
      ${customStyle}
    `,
  );
  handleWrapper.appendChild(handle);

  if (mode === 'column') {
    // Create resizer
    const resizer = document.createElement('span');
    resizer.classList.add(COLUMN_RESIZE_HANDLE);
    // We substract the width of the top and bottom borders to align the resizer with the table's top
    resizer.style.top = `${-cell.offsetTop}px`;
    resizer.style.height = `${table.clientHeight}px`;
    handleWrapper.appendChild(resizer);
  }

  cell.appendChild(handleWrapper);
}

/**
 * Add decorator to selected cells for edition.
 * @param cells: TablePlan containing each cells that will be decorated.
 */
export function decorateCellsAsSelected(cells: TablePlan, table: HTMLTableElement) {
  table.classList.add(WITH_SELECTED_CELLS);
  cells.forEach((row, i) => {
    const borderTop = i === 0;
    const borderBottom = i === cells.length - 1;
    row.forEach((cell, j) => {
      const borderLeft = j === 0;
      const borderRight = j === row.length - 1;
      decorateCellAsSelected(cell, borderTop, borderRight, borderBottom, borderLeft);
    });
  });
}

/**
 * Add decorator to selected cell for edition.
 * @param cell: cell to be decorated.
 * @param borderTop: flag to add a top border.
 * @param borderRight: flag to add a right border.
 * @param borderBottom: flag to add a bottom border.
 * @param borderLeft: flag to add a left border.
 */
function decorateCellAsSelected(
  cell: HTMLTableCellElement,
  borderTop: boolean,
  borderRight: boolean,
  borderBottom: boolean,
  borderLeft: boolean,
) {
  cell.classList.add(decoratedCellsClassnames.BORDERED);
  if (borderTop) {
    cell.classList.add(decoratedCellsClassnames.BORDERED_TOP);
  }
  if (borderRight) {
    cell.classList.add(decoratedCellsClassnames.BORDERED_RIGHT);
  }
  if (borderBottom) {
    cell.classList.add(decoratedCellsClassnames.BORDERED_BOTTOM);
  }
  if (borderLeft) {
    cell.classList.add(decoratedCellsClassnames.BORDERED_LEFT);
  }
  cell.setAttribute(EDITOR_SELECTED_ELEMENT, 'true');
}

export function decorateCellHeaderAsSelected(
  cell: HTMLTableCellElement,
  mode: 'row' | 'column',
  isOnlyOneSelected = false,
) {
  if (mode === 'row') {
    cell.classList.add(decoratedCellsClassnames.BORDERED_ROW);
  }
  if (mode === 'column') {
    cell.classList.add(decoratedCellsClassnames.BORDERED_COLUMN);
  }

  if (isOnlyOneSelected) {
    cell.classList.add(decoratedCellsClassnames.BORDERED_ROUNDED);
  }
}

export function undecorateAllTableForEdition(container: Element) {
  container.querySelectorAll('table').forEach(table => {
    if (isDecoratedTable(table)) {
      undecorateTableForEdition(table);
    }
  });
}

/**
 * Clean all decorators that were added to edit the table (inside and around it).
 */
export function undecorateTableForEdition(table: HTMLTableElement): void {
  // Remove all decorators inside table.
  undecorateInnerTable(table);
  // Get table out of its added wrapper.
  const { parentNode: wrapper } = table;
  if (!wrapper || !isTableWrapper(wrapper)) {
    return;
  }
  const { parentNode: grandParentNode } = wrapper;
  if (!grandParentNode) {
    return;
  }
  const savedSelection = createPointsWithSelectionInElement(wrapper);
  grandParentNode.insertBefore(getElementContents(wrapper), wrapper);
  grandParentNode.removeChild(wrapper);
  updateSelectionInElementWithPoints(table, savedSelection);
}

/**
 * Clean all decorators that were added inside the table.
 */
export function undecorateInnerTable(table: HTMLTableElement) {
  undecorateTableHeaders(table);
  undecorateCellsAsSelected(table);
  undecorateButtonPanels(table);
}

/**
 * Remove the table headers decorators.
 */
function undecorateTableHeaders(table: HTMLTableElement) {
  table.querySelectorAll(`.${headerClassnames.HEADER_WRAPPER}`).forEach(handle => handle.remove());
  table
    .querySelectorAll(`.${headerClassnames.WITH_HEADER}`)
    .forEach(cell => cell.classList.remove(headerClassnames.WITH_HEADER));
}

/**
 * Remove cells decorators that made them looked as selected.
 */
export function undecorateCellsAsSelected(table: HTMLTableElement) {
  table.classList.remove(WITH_SELECTED_CELLS);
  table.querySelectorAll(`.${decoratedCellsClassnames.BORDERED}`).forEach(cell => {
    cell.classList.remove(...Object.values(decoratedCellsClassnames));
    cell.removeAttribute(EDITOR_SELECTED_ELEMENT);
  });
}

export function undecorateButtonPanels(table: HTMLTableElement) {
  table.querySelectorAll(`.${buttonPanelClassnames.PANEL}`).forEach(el => el.remove());
}

/**
 * Append button panel for table deletion.
 * @param cell: cell to which button panel is appended.
 * @param table: table to which button panel is appended.
 * @param deleteElement: handler called when clicking on the 'delete' button.
 */
export function appendButtonPanelToDelete(
  cell: HTMLTableCellElement,
  table: HTMLTableElement,
  deleteElement: () => void,
) {
  const { ownerDocument: document } = cell;
  if (!document) {
    return;
  }
  const row = findAncestor(cell, isTableRowElement, table);
  if (!row) {
    return;
  }

  const buttonPanel = document.createElement('div');
  buttonPanel.setAttribute('contenteditable', 'false');
  buttonPanel.classList.add(buttonPanelClassnames.PANEL, buttonPanelClassnames.CORNER_BUTTON);
  appendButton(buttonPanel, document, trashIconContent, deleteElement);

  cell.appendChild(buttonPanel);
  cell.classList.add(decoratedCellsClassnames.WITH_BUTTON_PANEL);
}

/**
 * Append button panel for row/column addition/deletion.
 * @param cell: cell to which button panel is appended.
 * @param table: table to which button panel is appended.
 * @param mode: 'row' or 'column' (for alignment).
 * @param addBefore: handler called when clicking on the 'add before' button.
 * @param addAfter: handler called when clicking on the 'add after' button.
 * @param deleteElement: handler called when clicking on the 'delete' button.
 */
export function appendButtonPanel(
  cell: HTMLTableCellElement,
  table: HTMLTableElement,
  mode: 'row' | 'column',
  addBefore: () => void,
  addAfter: () => void,
  deleteElement: () => void,
) {
  const { ownerDocument: document } = cell;
  if (!document) {
    return;
  }
  const row = findAncestor(cell, isTableRowElement, table);
  if (!row) {
    return;
  }
  const positionButtonPanelBefore =
    mode === 'column'
      ? indexOf(row.cells, cell) > row.children.length / 2
      : indexOf(table.rows, row) > table.rows.length / 2;

  const buttonPanel = document.createElement('div');
  buttonPanel.setAttribute('contenteditable', 'false');
  buttonPanel.classList.add(buttonPanelClassnames.PANEL);
  buttonPanel.classList.add(
    mode === 'column' ? buttonPanelClassnames.COLUMN_DISPLAY : buttonPanelClassnames.ROW_DISPLAY,
  );
  if (positionButtonPanelBefore) {
    buttonPanel.classList.add(buttonPanelClassnames.BEFORE_DISPLAY);
  }
  appendButton(
    buttonPanel,
    document,
    mode === 'column' ? addColumnBeforeIconContent : addRowBeforeIconContent,
    addBefore,
  );
  appendButton(
    buttonPanel,
    document,
    mode === 'column' ? addColumnAfterIconContent : addRowAfterIconContent,
    addAfter,
  );
  appendButton(buttonPanel, document, trashIconContent, deleteElement);

  cell.appendChild(buttonPanel);
  cell.classList.add(decoratedCellsClassnames.WITH_BUTTON_PANEL);
}

function appendButton(buttonPanel: Element, document: Document, iconContent: string, onclick: () => void) {
  const button = document.createElement('span');
  button.classList.add(buttonPanelClassnames.BUTTON);
  button.innerHTML = iconContent;
  button.onclick = onclick;
  buttonPanel.appendChild(button);
}

/*
 * Style.
 */

const tableBorderRadius = '3px';

/**
 * Compute column's header style.
 * @param cell: table's cell the header is appended to.
 */
function getHeaderDecoratorStyle(
  table: HTMLTableElement,
  cell: HTMLTableCellElement,
  isFirstCell: boolean,
  isLastCell: boolean,
  direction: 'row' | 'column',
) {
  const prevBorderWidthOrNull =
    direction === 'row' ? getBorderTopWidthForElement(cell) : getBorderLeftWidthForElement(cell);
  const prevBorderWidth = prevBorderWidthOrNull === null ? 0 : prevBorderWidthOrNull;
  const border = isFirstCell
    ? ''
    : `${direction === 'row' ? 'border-top' : 'border-left'}: ${prevBorderWidth}px solid;`;
  const borderEndRadius = isLastCell
    ? `border-${direction === 'row' ? 'bottom-left' : 'top-right'}-radius: ${tableBorderRadius};`
    : '';
  return `
      ${border}
      ${direction === 'row' ? 'top' : 'left'}: -${prevBorderWidth}px;
      ${direction === 'row' ? 'height' : 'width'}: calc(100% + ${prevBorderWidth}px); 
      ${isFirstCell ? `border-top-left-radius: ${tableBorderRadius};` : ''}
      ${borderEndRadius}
    `;
}

function getTableWrapperStyle(maxBorderLeftWidth: number, maxBorderTopWidth: number) {
  const buttonPanelWidth = BUTTON_HEIGHT * 3 + BUTTON_MARGIN * 6 + BUTTON_PANEL_PADDING * 2;
  const buttonPanelHeight = BUTTON_HEIGHT + BUTTON_MARGIN * 2 + BUTTON_PANEL_PADDING * 2;
  return `
    position: relative;
    display: inline-block;
    cursor: default;
    padding-left: ${HEADER_DECORATOR_SIZE + maxBorderLeftWidth}px;
    padding-top: ${HEADER_DECORATOR_SIZE + maxBorderTopWidth}px;
    left: -${HEADER_DECORATOR_SIZE + maxBorderLeftWidth}px;
    top: -${HEADER_DECORATOR_SIZE + maxBorderLeftWidth}px;
    padding-right: 1px;
    padding-bottom: 1px;
    ${/* So that content bellow does not move, the '+ 5' was found experimentally. */ ''}
    margin-bottom: -${HEADER_DECORATOR_SIZE + maxBorderTopWidth + 5}px; 
    ${/* So that its children cannot go above its siblings, even with z-index > 0 */ ''}
    z-index: 0;
    
    table.${WITH_SELECTED_CELLS} *::selection {
      background: transparent;
    }

    .${headerClassnames.WITH_HEADER} {
      position: relative;
    }

    .${headerClassnames.HEADER}, .${TABLE_CORNER_BUTTON} {
      position: absolute;
      background-color: #f3f4f6;
      :hover {
        background-color: #d5d6d9;
      }
      border-color: ${DEFAULT_BORDER_COLOR};
    }

    .${TABLE_FULLY_SELECTED} .${TABLE_CORNER_BUTTON} {
      background-color: #367fee;
    }

    .${TABLE_CORNER_BUTTON} {
       top: -${HEADER_DECORATOR_SIZE + maxBorderTopWidth}px;
       left: -${HEADER_DECORATOR_SIZE + maxBorderLeftWidth}px;
       width: 10px; 
       height: 10px; 
       border-radius: 5px;
    }

    .${headerClassnames.COLUMN_DISPLAY} {
      top: -${HEADER_DECORATOR_SIZE + maxBorderTopWidth}px;
      height: ${HEADER_DECORATOR_SIZE}px;
    }

    .${headerClassnames.ROW_DISPLAY} {
      left: -${HEADER_DECORATOR_SIZE + maxBorderLeftWidth}px;
      width: ${HEADER_DECORATOR_SIZE}px;
    }

    .${decoratedCellsClassnames.BORDERED} {
      position: relative;
      background-color: #ebf2fd;

      &:before {
        content: '';
        position: absolute;
        width: calc(100% + 4px);
        height: calc(100% + 4px);
        left: -2px;
        top: -2px;
        border-color: #367fee;
        border-style: solid;
        border-width: 0;
        box-sizing: border-box;
        z-index: 1;
      }

      &.${decoratedCellsClassnames.BORDERED_TOP}:before {
        border-top-width: 3px;
      }
      &.${decoratedCellsClassnames.BORDERED_RIGHT}:before {
        border-right-width: 3px;
      }
      &.${decoratedCellsClassnames.BORDERED_BOTTOM}:before {
        border-bottom-width: 3px;
      }
      &.${decoratedCellsClassnames.BORDERED_LEFT}:before {
        border-left-width: 3px;
      }

      &.${decoratedCellsClassnames.BORDERED_ROW} .${headerClassnames.ROW_DISPLAY} {
        background-color: #367fee;
        z-index: 1;
        box-sizing: border-box;

        border-top: 3px #367fee solid;
        border-bottom: 3px #367fee solid;
        height: calc(100% + 4px);
        top: -2px;
      }

      &.${decoratedCellsClassnames.BORDERED_ROUNDED} .${headerClassnames.ROW_DISPLAY} {
        border-top-left-radius: ${tableBorderRadius};
        border-bottom-left-radius: ${tableBorderRadius};
      }

      &.${decoratedCellsClassnames.BORDERED_ROUNDED} .${headerClassnames.COLUMN_DISPLAY} {
        border-top-left-radius: ${tableBorderRadius};
        border-top-right-radius: ${tableBorderRadius};
      }

      &.${decoratedCellsClassnames.BORDERED_COLUMN} .${headerClassnames.COLUMN_DISPLAY} {
        background-color: #367fee;
        z-index: 1;
        box-sizing: border-box;

        border-left: 3px #367fee solid;
        border-right: 3px #367fee solid;
        width: calc(100% + 4px);
        left: -2px;
      }
    }
    
    .${decoratedCellsClassnames.WITH_BUTTON_PANEL} {
      z-index: 2;
    }

    .${buttonPanelClassnames.PANEL} {
      position: absolute;
      background-color: #1a1a1a;
      border-radius: 5px;
      z-index: 1;
      white-space: nowrap;
      padding: ${BUTTON_PANEL_PADDING}px;

      .${buttonPanelClassnames.BUTTON} {
        display: inline-block;
        height: ${BUTTON_HEIGHT}px;
        width: ${BUTTON_HEIGHT}px;
        vertical-align: top;
        margin: ${BUTTON_MARGIN}px;
        border-radius: 4px;
        color: white;
        text-align: center;
        line-height: ${BUTTON_HEIGHT}px;

        &:hover {
          background-color: #3b3b3c;
        }
        
        svg {
          position: relative;
          top: 50%;
          transform: translate(0, -50%);
        }
      }
    }

    .${buttonPanelClassnames.COLUMN_DISPLAY} {
      top: -${HEADER_DECORATOR_SIZE + maxBorderTopWidth}px;

      &.${buttonPanelClassnames.BEFORE_DISPLAY} {
        left: -${buttonPanelWidth + 10}px;
      }

      &:not(.${buttonPanelClassnames.BEFORE_DISPLAY}) {
        right: -${buttonPanelWidth + 10}px;
      }
    }

    .${buttonPanelClassnames.ROW_DISPLAY} {
      left: -${HEADER_DECORATOR_SIZE + maxBorderLeftWidth}px;

      &.${buttonPanelClassnames.BEFORE_DISPLAY} {
        top: -${buttonPanelHeight + 10}px;
      }

      &:not(.${buttonPanelClassnames.BEFORE_DISPLAY}) {
        bottom: -${buttonPanelHeight + 10}px;
      }
    }

    .${buttonPanelClassnames.CORNER_BUTTON} {
      top: ${HEADER_DECORATOR_SIZE}px;
      left: -${HEADER_DECORATOR_SIZE + maxBorderLeftWidth}px;
    }
    
    .${COLUMN_RESIZE_HANDLE} {
      position: absolute;
      cursor: col-resize;
      right: -5px;
      width: 5px;
      z-index: 10;
      opacity: 0;
    }
  }
  `;
}
